import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared";
import { AiInsightsCard } from "@/components/AiInsightsCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sparkles, Send, Loader2, ShieldAlert, BookOpen, Settings2, ScrollText, Dog as DogIcon, Info,
} from "lucide-react";
import { toast } from "sonner";

export default function AI() {
  const { user } = useAuth();
  const role = user?.role;
  const isGlobal = ["super_admin", "head_office_admin"].includes(role);
  const isManager = ["franchise_owner", "location_manager"].includes(role);
  const isTrainer = role === "trainer";
  const isClient = role === "client";
  const [disabled, setDisabled] = useState(false);

  if (disabled) return <div className="text-center py-20 text-muted-foreground" data-testid="ai-disabled">HV AI is currently disabled by Head Office.</div>;

  return (
    <div>
      <PageHeader title="HV AI" subtitle="Your controlled Hundeverket assistant — grounded in your data and the HV method, scoped to your role." />
      {isGlobal ? (
        <Tabs defaultValue="assistant">
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value="assistant" data-testid="ai-tab-assistant"><Sparkles size={14} className="mr-1.5" /> Assistant</TabsTrigger>
            <TabsTrigger value="knowledge" data-testid="ai-tab-knowledge"><BookOpen size={14} className="mr-1.5" /> Knowledge Base</TabsTrigger>
            <TabsTrigger value="settings" data-testid="ai-tab-settings"><Settings2 size={14} className="mr-1.5" /> Settings</TabsTrigger>
            <TabsTrigger value="logs" data-testid="ai-tab-logs"><ScrollText size={14} className="mr-1.5" /> Logs</TabsTrigger>
          </TabsList>
          <TabsContent value="assistant" className="mt-4"><Assistant {...{ isManager: true, isTrainer, isClient, setDisabled }} showInsights /></TabsContent>
          <TabsContent value="knowledge" className="mt-4"><KnowledgeBase /></TabsContent>
          <TabsContent value="settings" className="mt-4"><AiSettings /></TabsContent>
          <TabsContent value="logs" className="mt-4"><AiLogs /></TabsContent>
        </Tabs>
      ) : (
        <Assistant {...{ isManager, isTrainer, isClient, setDisabled }} showInsights={isManager} />
      )}
    </div>
  );
}

function Assistant({ isManager, isTrainer, isClient, setDisabled, showInsights }) {
  const query = new URLSearchParams(useLocation().search);
  const [prompts, setPrompts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [dogs, setDogs] = useState([]);
  const [dogId, setDogId] = useState(query.get("dog") || "");
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/ai/suggested-prompts").then((r) => setPrompts(r.data)).catch((e) => { if (e?.response?.status === 403) setDisabled(true); });
    if (isTrainer || isClient) api.get("/dogs").then((r) => setDogs(r.data)).catch(() => {});
  }, [isTrainer, isClient, setDisabled]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  const send = async (text, type = "chat") => {
    const prompt = (text ?? input).trim();
    if (!prompt || sending) return;
    setMessages((m) => [...m, { role: "user", text: prompt }]);
    setInput(""); setSending(true);
    try {
      const r = await api.post("/ai/chat", { prompt, interaction_type: type, related_dog_id: dogId || null });
      setMessages((m) => [...m, { role: "ai", text: r.data.response }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "HV AI could not respond. Please try again.";
      setMessages((m) => [...m, { role: "ai", text: msg, error: true }]);
      if (e?.response?.status === 403) setDisabled(true); else toast.error(msg);
    }
    setSending(false);
  };

  const needsDog = isTrainer || isClient;

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 flex flex-col">
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 mb-4 text-xs text-amber-900" data-testid="ai-disclaimer">
          <ShieldAlert size={15} className="shrink-0 mt-0.5" />
          <span>HV AI responses are based on your Hundeverket data and the HV method. AI guidance supports but does not replace professional trainer judgement, and does not diagnose medical or behaviour issues — contact a trainer for behaviour concerns.</span>
        </div>

        <Card className="border-border flex-1 flex flex-col min-h-[420px]">
          <CardContent className="p-0 flex flex-col flex-1">
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 max-h-[52vh]">
              {messages.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground py-10">
                  <div className="h-12 w-12 rounded-2xl bg-neutral-900 flex items-center justify-center mb-3"><Sparkles size={22} className="text-amber-300" /></div>
                  <p className="text-sm max-w-sm">Ask HV AI anything about your {isClient ? "dog and homework" : isTrainer ? "classes and dogs" : isManager ? "location's operations" : "network"}. Try a suggested prompt to get started.</p>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : m.error ? "bg-red-50 text-red-800 border border-red-200" : "bg-secondary text-secondary-foreground"}`}>
                    {m.role === "ai" ? <div className="prose prose-sm max-w-none prose-p:my-1.5 prose-ul:my-1.5 prose-li:my-0.5 prose-headings:text-sm prose-headings:font-heading prose-headings:mt-2"><ReactMarkdown>{m.text}</ReactMarkdown></div> : m.text}
                  </div>
                </div>
              ))}
              {sending && <div className="flex justify-start"><div className="rounded-2xl px-4 py-2.5 bg-secondary text-muted-foreground text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> HV AI is thinking…</div></div>}
            </div>

            <div className="border-t border-border p-3">
              {needsDog && (
                <div className="mb-2 flex items-center gap-2">
                  <DogIcon size={14} className="text-muted-foreground" />
                  <Select value={dogId || "none"} onValueChange={(v) => setDogId(v === "none" ? "" : v)}>
                    <SelectTrigger className="h-8 w-56" data-testid="ai-dog-select"><SelectValue placeholder="Context: no dog selected" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No specific dog</SelectItem>
                      {dogs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
                <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask HV AI…" data-testid="ai-input" />
                <Button type="submit" disabled={sending || !input.trim()} className="gap-1.5" data-testid="ai-send"><Send size={15} /> Send</Button>
              </form>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        {showInsights && <AiInsightsCard />}
        <Card className="border-border">
          <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><Sparkles size={16} strokeWidth={1.5} /> Suggested Prompts</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {prompts.map((p, i) => (
              <button key={i} onClick={() => send(p.prompt, p.type)} disabled={sending}
                className="w-full text-left text-sm rounded-lg border border-border/70 px-3 py-2 hover:bg-secondary/60 hover:border-primary/40 transition-colors disabled:opacity-50" data-testid="ai-suggested-prompt">
                {p.title}
              </button>
            ))}
            {prompts.length === 0 && <p className="text-sm text-muted-foreground py-2">Loading prompts…</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ---------- Head Office: Knowledge Base ---------- */
function KnowledgeBase() {
  const [kb, setKb] = useState(null);
  useEffect(() => { api.get("/ai/knowledge-base").then((r) => setKb(r.data)).catch(() => {}); }, []);
  if (!kb) return <div className="text-center py-12 text-muted-foreground">Loading…</div>;
  const items = [
    { label: "Training Programs", value: kb.programs }, { label: "Lessons", value: kb.lessons },
    { label: "Exercises", value: kb.exercises }, { label: "Homework Templates", value: kb.homework_templates },
    { label: "Academy Courses", value: kb.academy_courses }, { label: "Quality Records", value: kb.quality_rules },
  ];
  return (
    <div>
      <Card className="border-border mb-4"><CardContent className="p-4 flex items-center gap-3 text-sm text-muted-foreground">
        <Info size={16} className="shrink-0" /> HV AI is grounded in the content below and answers strictly within each user's role scope. Model: <span className="font-medium text-foreground">{kb.model}</span>
      </CardContent></Card>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => (
          <Card key={it.label} className="border-border" data-testid="kb-item"><CardContent className="p-4">
            <div className="text-2xl font-heading font-semibold">{it.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{it.label}</div>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

/* ---------- Head Office: Settings ---------- */
const SETTING_FIELDS = [
  ["use_method_content", "Use HV Method content", "Ground answers in programs, lessons and exercises."],
  ["use_academy_content", "Use HV Academy content", "Include certification and academy progress in insights."],
  ["use_quality_data", "Use quality data", "Include quality issues and scores in network insights."],
  ["allow_client_ai", "Allow client-facing AI", "Let dog owners use HV AI for their own dogs and homework."],
  ["require_trainer_review_for_homework", "Require trainer review for AI homework", "AI-drafted homework is flagged for trainer review before sharing."],
];
function AiSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/ai/knowledge-settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async (patch) => {
    const next = { ...s, ...patch }; setS(next);
    try { await api.put("/ai/knowledge-settings", next); toast.success("AI settings updated"); }
    catch { toast.error("Failed to save"); }
  };
  if (!s) return <div className="text-center py-12 text-muted-foreground">Loading…</div>;
  return (
    <Card className="border-border max-w-2xl"><CardContent className="p-5 space-y-1">
      {SETTING_FIELDS.map(([key, title, desc]) => (
        <div key={key} className="flex items-center justify-between py-3 border-b border-border/50 last:border-0" data-testid={`ai-setting-${key}`}>
          <div className="pr-4"><Label className="text-sm font-medium">{title}</Label><p className="text-xs text-muted-foreground mt-0.5">{desc}</p></div>
          <Switch checked={!!s[key]} onCheckedChange={(v) => save({ [key]: v })} data-testid={`ai-toggle-${key}`} />
        </div>
      ))}
    </CardContent></Card>
  );
}

/* ---------- Head Office: Logs ---------- */
function AiLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(false);
  const PAGE = 50;

  const fetchPage = useCallback(async (skip) => {
    setLoading(true);
    try {
      const r = await api.get(`/ai/logs?skip=${skip}&limit=${PAGE}`);
      setLogs((prev) => (skip === 0 ? r.data.logs : [...prev, ...r.data.logs]));
      setTotal(r.data.total); setHasMore(r.data.has_more); setRetention(r.data.retention_days);
    } catch (e) { console.error("Failed to fetch AI logs:", e); }
    setLoading(false);
  }, []);
  useEffect(() => { fetchPage(0); }, [fetchPage]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 text-xs text-muted-foreground">
        <span data-testid="ai-logs-count">Showing {logs.length} of {total} interactions</span>
        {retention != null && <span>Audit retention: {retention} days</span>}
      </div>
      <Card className="border-border"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="text-left font-semibold px-4 py-3">User</th><th className="text-left font-semibold px-4 py-3">Role</th><th className="text-left font-semibold px-4 py-3">Type</th><th className="text-left font-semibold px-4 py-3 hidden md:table-cell">Summary</th><th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">When</th></tr>
          </thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id} className="border-b border-border/50" data-testid="ai-log-row">
                <td className="px-4 py-3 font-medium">{l.user_name}</td>
                <td className="px-4 py-3 capitalize text-muted-foreground">{String(l.user_role).replace(/_/g, " ")}</td>
                <td className="px-4 py-3 capitalize">{String(l.interaction_type).replace(/_/g, " ")}</td>
                <td className="px-4 py-3 hidden md:table-cell text-muted-foreground max-w-md truncate">{l.response_summary}{l.dog_name ? ` · ${l.dog_name}` : ""}</td>
                <td className="px-4 py-3 hidden sm:table-cell text-muted-foreground text-xs">{new Date(l.created_at).toLocaleString()}</td>
              </tr>
            ))}
            {logs.length === 0 && <tr><td colSpan={5} className="text-center text-muted-foreground py-10">No AI activity yet.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
      {hasMore && (
        <div className="flex justify-center mt-4">
          <Button variant="outline" size="sm" onClick={() => fetchPage(logs.length)} disabled={loading} data-testid="ai-logs-load-more">
            {loading ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}
